import { module } from 'constant'
import { resizerSelectors } from 'selectors'
import { number, sum } from 'utils'
import * as Highcharts from 'highcharts'
import 'highcharts/highcharts-more'
import 'highcharts/modules/solid-gauge'
import 'highcharts/modules/funnel'
import 'highcharts/modules/exporting'
import 'highcharts/modules/offline-exporting'
import 'highcharts/modules/export-data'
import 'highcharts/modules/heatmap'
import 'highcharts/modules/wordcloud'
import 'highcharts/modules/boost'
import 'highcharts/highcharts-3d'
import 'highcharts/modules/map'
import 'highcharts/modules/stock'
import 'highcharts/modules/annotations'
import 'highcharts/modules/accessibility'
import 'highcharts/modules/no-data-to-display'
// @ts-ignore
import * as HC_regression from 'highcharts-regression'
import { constant } from '@/component/gridstack/constant'

Highcharts.setOptions({
	boost: {
		enabled: true,
		useGPUTranslations: true,
		usePreallocated: true,
		seriesThreshold: 50,
	},
	lang: {
		noData: window.appConfig.translations['core.highcharts.no-data'],
	},
	plotOptions: {
		series: {
			animation: false,
		},
	},
})

// eslint-disable-next-line func-names
Highcharts.SVGRenderer.prototype.symbols.targetLineVertical = function (
	x: number,
	y: number,
	w: number,
	h: number,
): (string | number)[] {
	return ['M', x + w / 2, y + h / 2 - 12, 'L', x + w / 2, y + h / 2 + 12]
}

// eslint-disable-next-line func-names
Highcharts.SVGRenderer.prototype.symbols.targetLineHorizontal = function (
	x: number,
	y: number,
	w: number,
	h: number,
): (string | number)[] {
	return ['M', x + w / 2 - 12, y + h / 2, 'L', x + w / 2 + 12, y + h / 2]
}

declare global {
	interface Window {
		Highcharts: typeof Highcharts
		charts: {
			charts: Array<Highcharts.Options & { blockId: string }>
			maps: Highcharts.Options[]
			stockCharts: Highcharts.Options[]
		}
	}
}

window.Highcharts = Highcharts
window.charts = {
	charts: [],
	maps: [],
	stockCharts: [],
}

const {
	resizer: { selector },
} = module
const FALLBACK_CHART_HEIGHT_PX = 390

export const Charts = (() => {
	const getChartDynamicContentHeight = (block: HTMLElement, isAutoheight: boolean) => {
		const blockContent = resizerSelectors.getItemBlockContent(block)
		if (!blockContent) {
			return 0
		}
		const { childNodes } = blockContent

		if (!childNodes) {
			return 0
		}
		for (const childNode of Array.from(childNodes)) {
			if (childNode.nodeType === Node.TEXT_NODE) {
				continue
			}
			if (!isAutoheight) {
				continue
			}
		}
		const minuses = resizerSelectors.getHeightMinus(block)
		let result = 0
		if (!minuses) {
			return result
		}
		for (let i = 0, { length } = minuses; i < length; i++) {
			const minus = minuses[i]
			const { marginTop, marginBottom } = window.getComputedStyle(minus)
			result -= minuses[i].offsetHeight + number.getNumberByPx(marginTop) + number.getNumberByPx(marginBottom)
		}
		return result
	}

	const update = (): Promise<(Highcharts.Chart | Highcharts.StockChart | Highcharts.MapChart)[] | void> => {
		let promises: Promise<Highcharts.Chart | Highcharts.StockChart | Highcharts.MapChart>[] = []
		if (!window.Highcharts) {
			// eslint-disable-next-line no-console
			console.error('Highcharts missing!')
			return Promise.resolve()
		}
		const { charts } = window.Highcharts
		if (!charts) {
			return Promise.resolve()
		}
		for (const chart of charts) {
			if (!chart) {
				continue
			}
			promises = [
				...promises,
				new Promise<Highcharts.Chart | Highcharts.StockChart | Highcharts.MapChart>((resolve) => {
					setTimeout(() => {
						fixChartSize(chart)
						resolve(chart)
					}, 0)
				}),
			]
		}
		return Promise.all(promises)
	}

	const fixChartSize = (chart: Highcharts.Chart | Highcharts.MapChart) => {
		const container = chart.container as HTMLElement
		if (!container) {
			return
		}
		const block = container.closest<HTMLElement>(selector.itemBlock)
		if (!block) {
			return
		}
		const item = container.closest<HTMLElement>(selector.item)
		if (!item) {
			return
		}
		const isAutoheight = Number(item.getAttribute(constant.selector.autoHeight)) === 1
		const header = resizerSelectors.getItemHead(block)
		const footer = resizerSelectors.getItemFooter(block)
		const { paddingLeft, paddingRight } = window.getComputedStyle(block)
		const chartHeight = sum.getSumByArgs(
			block.offsetHeight,
			header ? -header.offsetHeight : 0,
			footer ? -footer.offsetHeight : 0,
			getChartDynamicContentHeight(block, isAutoheight),
		)
		chart.update({
			chart: {
				width:
					block.offsetWidth -
					(paddingLeft ? parseFloat(paddingLeft) : 0) -
					(paddingRight ? parseFloat(paddingRight) : 0),
				height: isAutoheight && chartHeight < FALLBACK_CHART_HEIGHT_PX ? FALLBACK_CHART_HEIGHT_PX : chartHeight,
			},
		})
	}

	const init = () => {
		let promises: Promise<Highcharts.Chart | Highcharts.StockChart | Highcharts.MapChart | null>[] = []
		for (const options of window.charts.charts) {
			promises = [
				...promises,
				new Promise<Highcharts.Chart | null>((resolve) => {
					setTimeout(() => {
						try {
							const chart = window.Highcharts.chart(options)
							fixChartSize(chart)
							resolve(chart)
						} catch (error) {
							// eslint-disable-next-line no-console
							console.error(error)
							resolve(null)
						}
					}, 0)
				}),
			]
		}
		for (const options of window.charts.stockCharts) {
			promises = [
				...promises,
				new Promise<Highcharts.StockChart | null>((resolve) => {
					setTimeout(() => {
						try {
							const chart = window.Highcharts.stockChart(options)
							fixChartSize(chart)
							resolve(chart)
						} catch (error) {
							// eslint-disable-next-line no-console
							console.error(error)
							resolve(null)
						}
					}, 0)
				}),
			]
		}
		for (const options of window.charts.maps) {
			promises = [
				...promises,
				new Promise<Highcharts.MapChart | null>((resolve) => {
					setTimeout(() => {
						try {
							const chart = window.Highcharts.mapChart(options)
							fixChartSize(chart)
							resolve(chart)
						} catch (error) {
							// eslint-disable-next-line no-console
							console.error(error)
							resolve(null)
						}
					}, 0)
				}),
			]
		}
		return Promise.all(promises).then((results) => results.filter((chart) => chart !== null))
	}

	const updateRemovedBlocks = (payload: any) => {
		if (payload && payload.removedBlocks) {
			const { removedBlocks } = payload
			for (const removedBlock of removedBlocks) {
				window.charts.charts = window.charts.charts.filter(
					(chart) => chart.blockId !== removedBlock && 'blockId' in chart,
				)
			}
		}
	}

	return {
		update,
		init,
		updateRemovedBlocks,
	}
})()
